import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';
import { SUBMIT_FEEDBACK_MUTATION } from '../../../graphql/resolvers/storyAnalysis/mutations/submitFeedback.gql';
import type {
  SentimentCorrection,
  ThumbSignal,
  TopicCorrection,
} from '../../../interfaces/analysisFeedback';

// ─── Props ────────────────────────────────────────────────────────────────────

interface StoryAnalyzerFeedbackFormProps {
  jobId: string | null;
  storyId: string | null;
  draftId: string;
  userId: string;
  detectedTopic?: string | null;
  detectedSentiment?: string | null;
}

// ─── Sub-component: ThumbRow ──────────────────────────────────────────────────

interface ThumbRowProps {
  label: string;
  sublabel?: string;
  value: ThumbSignal | null;
  onChange: (val: ThumbSignal) => void;
  disabled?: boolean;
}

const ThumbRow = ({
  label,
  sublabel,
  value,
  onChange,
  disabled,
}: ThumbRowProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
    }}
  >
    <div>
      <p>{label}</p>
      {sublabel && <p>{sublabel}</p>}
    </div>
    <div>
      <span title="Correct">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange('up')}
          style={{
            minWidth: '40px',
            padding: '0.25rem 0.5rem',
            borderRadius: '8px',
            border: value === 'up' ? 'none' : '1px solid #2e7d32',
            backgroundColor: value === 'up' ? '#2e7d32' : 'transparent',
            color: value === 'up' ? '#fff' : '#2e7d32',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {value === 'up' ? '👍' : '👍'}
        </button>
      </span>
      <span title="Incorrect">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange('down')}
          style={{
            minWidth: '40px',
            padding: '0.25rem 0.5rem',
            borderRadius: '8px',
            border: value === 'down' ? 'none' : '1px solid #c62828',
            backgroundColor: value === 'down' ? '#c62828' : 'transparent',
            color: value === 'down' ? '#fff' : '#c62828',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {value === 'down' ? '👎' : '👎'}
        </button>
      </span>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const StoryAnalyzerFeedbackForm = ({
  jobId,
  storyId,
  draftId,
  userId,
  detectedTopic,
  detectedSentiment,
}: StoryAnalyzerFeedbackFormProps): React.ReactElement => {
  const [overallThumb, setOverallThumb] = useState<ThumbSignal | null>(null);
  const [overallComment, setOverallComment] = useState('');

  const [sentimentThumb, setSentimentThumb] = useState<ThumbSignal | null>(
    null,
  );
  const [sentimentCorrection, setSentimentCorrection] = useState<
    SentimentCorrection | ''
  >('');

  const [topicThumb, setTopicThumb] = useState<ThumbSignal | null>(null);
  const [topicCorrection, setTopicCorrection] = useState<TopicCorrection | ''>(
    '',
  );

  const [submitted, setSubmitted] = useState(false);
  const [retrainingTriggered, setRetrainingTriggered] = useState(false);

  const [submitFeedback, { loading, error }] = useMutation(
    SUBMIT_FEEDBACK_MUTATION,
    {
      onCompleted: (data) => {
        setSubmitted(true);
        setRetrainingTriggered(
          data?.submitFeedback?.retrainingTriggered ?? false,
        );
      },
    },
  );

  const hasAnySignal =
    overallThumb !== null || sentimentThumb !== null || topicThumb !== null;

  const handleSubmit = () => {
    submitFeedback({
      variables: {
        input: {
          jobId,
          storyId,
          draftId,
          userId,
          ...(overallThumb && { overallThumb }),
          ...(overallComment.trim() && {
            overallComment: overallComment.trim(),
          }),
          ...(sentimentThumb && { sentimentThumb }),
          ...(sentimentThumb === 'down' &&
            sentimentCorrection && { sentimentCorrection }),
          ...(topicThumb && { topicThumb }),
          ...(topicThumb === 'down' && topicCorrection && { topicCorrection }),
        },
      },
    });
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem 0',
          gap: '1rem',
          textAlign: 'center',
        }}
      >
        <span style={{ fontSize: '3.5rem' }}>✅</span>
        <p>Thanks for your feedback!</p>
        <p>
          Your signal helps improve the AI models over time.
          {retrainingTriggered && (
            <> A retraining job has been queued automatically.</>
          )}
        </p>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Section: Overall Score ── */}
      <div>
        <small
          style={{
            letterSpacing: '1.5px',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            color: '#666',
          }}
        >
          Overall Score
        </small>
        <ThumbRow
          label="Was the overall score accurate?"
          sublabel="Score shown in the header above"
          value={overallThumb}
          onChange={setOverallThumb}
          disabled={loading}
        />
        {overallThumb !== null && (
          <textarea
            disabled={loading}
            placeholder="Tell us more about what was inaccurate..."
            rows={2}
            style={{
              width: '100%',
              marginTop: '0.5rem',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ccc',
              resize: 'vertical',
            }}
            value={overallComment}
            onChange={(e) => setOverallComment(e.target.value)}
          />
        )}
      </div>

      <hr style={{ margin: '0.75rem 0' }} />

      {/* ── Section: Sentiment ── */}
      <div>
        <small
          style={{
            letterSpacing: '1.5px',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            color: '#666',
          }}
        >
          Sentiment
        </small>
        <ThumbRow
          label="Was the sentiment label correct?"
          sublabel={
            detectedSentiment
              ? `Detected: ${detectedSentiment}`
              : 'Positive / Neutral / Negative classification'
          }
          value={sentimentThumb}
          onChange={(val) => {
            setSentimentThumb(val);
            if (val === 'up') setSentimentCorrection('');
          }}
          disabled={loading}
        />
        {sentimentThumb === 'down' && (
          <div style={{ marginTop: '0.5rem' }}>
            <label>
              What should the sentiment be?
              <select
                disabled={loading}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
                value={sentimentCorrection}
                onChange={(e) =>
                  setSentimentCorrection(e.target.value as SentimentCorrection)
                }
              >
                <option value="">Select…</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <hr style={{ margin: '0.75rem 0' }} />

      {/* ── Section: Topic ── */}
      <div>
        <small
          style={{
            letterSpacing: '1.5px',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            color: '#666',
          }}
        >
          Topic
        </small>
        <ThumbRow
          label="Was the topic classification correct?"
          sublabel={
            detectedTopic
              ? `Detected: ${detectedTopic.replace(/_/g, ' ')}`
              : 'Politics / Health / Technology / etc.'
          }
          value={topicThumb}
          onChange={(val) => {
            setTopicThumb(val);
            if (val === 'up') setTopicCorrection('');
          }}
          disabled={loading}
        />
        {topicThumb === 'down' && (
          <div style={{ marginTop: '0.5rem' }}>
            <label>
              What should the topic be?
              <select
                disabled={loading}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
                value={topicCorrection}
                onChange={(e) =>
                  setTopicCorrection(e.target.value as TopicCorrection)
                }
              >
                <option value="">Select…</option>
                <option value="politics">Politics</option>
                <option value="social_issues">Social Issues</option>
                <option value="health">Health</option>
                <option value="technology">Technology</option>
                <option value="environment">Environment</option>
                <option value="economics">Economics</option>
                <option value="personal">Personal</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          role="alert"
          className="alert alert--error"
          style={{ marginBottom: '0.5rem' }}
        >
          Failed to submit feedback. Please try again.
        </div>
      )}

      {/* ── Submit ── */}
      <button
        type="button"
        disabled={!hasAnySignal || loading}
        onClick={handleSubmit}
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: '#F5A623',
          color: '#000',
          fontWeight: 700,
          borderRadius: '8px',
          border: 'none',
          cursor: !hasAnySignal || loading ? 'default' : 'pointer',
          opacity: !hasAnySignal || loading ? 0.5 : 1,
        }}
      >
        {loading ? (
          <span className="spinner" role="status" aria-label="loading" />
        ) : (
          'Submit Feedback'
        )}
      </button>

      <small
        style={{
          display: 'block',
          textAlign: 'center',
          marginTop: '0.5rem',
          color: '#666',
        }}
      >
        Feedback is used to improve AI model accuracy over time. At least one
        signal required to submit.
      </small>
    </div>
  );
};
